/**
 * Post-login return-URL handling.
 *
 * A user who deep-links into the app while signed out is bounced to /login and
 * must land back on the EXACT page they asked for — including its query string
 * and hash. `/settings?tab=notifications` that returns as `/settings` has lost
 * the only part of the URL the user cared about.
 *
 * Every capture site (auth guards, the session-expiry bounce) and every restore
 * site (LoginPage, PublicRoute, the SSO/token-exchange callbacks) goes through
 * this module, so the three parts of the URL can't drift apart again.
 */

/** The subset of react-router's `Location` a return URL is built from. */
export interface ReturnUrlLocation {
  pathname: string;
  search?: string;
  hash?: string;
}

/** Where a user with no remembered destination lands. */
export const DEFAULT_RETURN_PATH = '/';

/**
 * Serialize a location into a same-origin path, keeping query string AND hash.
 */
export function buildReturnPath(location: ReturnUrlLocation): string {
  return `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
}

/**
 * Reject anything that isn't a same-origin path, so a remembered destination
 * can never be used as an open redirect.
 *
 * Blocked: absolute URLs (`https://evil.com`), protocol-relative (`//evil.com`),
 * the backslash variant browsers normalize to `//` (`/\evil.com`), and any
 * scheme-ish value (`javascript:alert(1)`).
 *
 * **Prefix-matching the raw string is not enough.** The URL parser strips every
 * ASCII tab, CR and LF from a URL *wherever they appear* before parsing it, so
 * `/\t/evil.com` clears a `startsWith('//')` check and only collapses into
 * `//evil.com` later, when it is assigned to `location.href`. We therefore
 * resolve the value the way the browser will and compare the resulting origin,
 * then hand back the parser's own normalized path — never the raw string.
 */
export function sanitizeReturnPath(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_RETURN_PATH;
  // A path, not a URL and not a relative reference: `evil.com` would otherwise
  // resolve to the same-origin `/evil.com` and be waved through.
  if (!raw.startsWith('/')) return DEFAULT_RETURN_PATH;

  const origin = window.location.origin;
  let resolved: URL;
  try {
    resolved = new URL(raw, origin);
  } catch {
    return DEFAULT_RETURN_PATH;
  }
  if (resolved.origin !== origin) return DEFAULT_RETURN_PATH;

  return `${resolved.pathname}${resolved.search}${resolved.hash}`;
}

/**
 * Read the destination an auth guard stashed in router state
 * (`<Navigate state={{ from: location }} />`) as a full, safe path.
 */
export function getReturnPathFromState(state: unknown): string {
  const from = (state as { from?: ReturnUrlLocation } | null | undefined)?.from;
  if (!from || typeof from.pathname !== 'string') return DEFAULT_RETURN_PATH;
  return sanitizeReturnPath(buildReturnPath(from));
}

/**
 * The external portal's own auth pages. Returning to one of them after signing
 * in is a loop, so they resolve to the portal home instead.
 */
const PORTAL_AUTH_SEGMENTS = new Set([
  'login',
  'register',
  'verify-email',
  'forgot-password',
  'reset-password',
]);

/** Where a portal user with no remembered destination lands. */
export function portalHomePath(tenantSlug: string): string {
  return `/external/${tenantSlug}`;
}

/**
 * Same as {@link sanitizeReturnPath}, but for the external customer portal:
 * the destination must also live under THIS portal's own route subtree.
 *
 * Same-origin is not a strong enough bound here. An external user's session is
 * scoped to one tenant's portal, so `/settings` (an internal-app route) or
 * `/external/other-tenant/orders` are both destinations they may not have, and
 * bouncing them there after login renders a surface their token will be
 * refused on. The path is therefore accepted only when its first two segments
 * are `external` + this tenant's slug.
 */
export function sanitizePortalReturnPath(
  raw: string | null | undefined,
  tenantSlug: string | undefined
): string {
  if (!tenantSlug) return DEFAULT_RETURN_PATH;
  const home = portalHomePath(tenantSlug);

  const safe = sanitizeReturnPath(raw);
  if (safe === DEFAULT_RETURN_PATH) return home;

  // `safe` is already same-origin and normalized, so this parse cannot fail.
  const { pathname } = new URL(safe, window.location.origin);
  const [, section, slug, page] = pathname.split('/');
  if (section !== 'external' || decodeSegment(slug) !== tenantSlug) return home;
  if (page !== undefined && PORTAL_AUTH_SEGMENTS.has(page)) return home;

  return safe;
}

/** Read an external-portal destination stashed in router state by a guard. */
export function getPortalReturnPathFromState(
  state: unknown,
  tenantSlug: string | undefined
): string {
  return sanitizePortalReturnPath(getReturnPathFromState(state), tenantSlug);
}

/** A lone `%` is a valid path character but not valid percent-encoding. */
function decodeSegment(segment: string | undefined): string | undefined {
  if (segment === undefined) return undefined;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}
